import { lazy, Suspense } from 'react';
import { Navigate, redirect, RouteObject } from 'react-router-dom';
import { fetchEnforcementTabListQuery } from '~/apis/performance/_counterfeit/enforcementAnalysis';
import LoadingCircularProgress from '~/components/LoadingCircularProgress';
import { AnalyticsRouteEnum, RootRouteEnum } from '~/router/@types';
import { PermissionMap } from '~/router/permissionMap';

const PerformanceOverviewPage = lazy(() => import('~/pages/Performance/Overview'));
const PerformanceSaturationRatePage = lazy(() => import('~/pages/Performance/_counterfeit/SaturationRate'));
const PerformanceCategoryShareSnapshotPage = lazy(
  () => import('~/pages/Performance/_counterfeit/CategoryShareSnapshot')
);
const PerformanceEnforcementAnalysisPage = lazy(() => import('~/pages/Performance/_counterfeit/EnforcementAnalysis'));
const PerformanceSellerAnalysisPage = lazy(() => import('~/pages/Performance/_counterfeit/SellerAnalysis'));
const PerformanceMarketplaceComplianceRatePage = lazy(
  () => import('~/pages/Performance/_counterfeit/MarketplaceComplianceRate')
);
const PerformanceReportsPage = lazy(() => import('~/pages/Performance/Reports'));
const PiracySocialMediaPerformanceEnforcementAnalysisPage = lazy(
  () => import('~/pages/Performance/_piracy/SocialMedia/EnforcementAnalysis')
);
const PiracySocialMediaPerformanceComplianceRatePage = lazy(
  () => import('~/pages/Performance/_piracy/SocialMedia/ComplianceRate')
);
const PiracyWebsiteContentPerformanceCleanlinessRatePage = lazy(
  () => import('~/pages/Performance/_piracy/WebsiteContent/CleanlinessRate')
);
const PiracyWebsiteContentPerformanceComplianceRatePage = lazy(
  () => import('~/pages/Performance/_piracy/WebsiteContent/ComplianceRate')
);
const PiracyWebsiteContentPerformanceEnforcementAnalysisPage = lazy(
  () => import('~/pages/Performance/_piracy/WebsiteContent/EnforcementAnalysis')
);
const PerformanceBrandExposurePage = lazy(() => import('~/pages/Performance/BrandExposure'));

export const PERFORMANCE_ROUTES: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="performance" replace />,
  },
  {
    path: AnalyticsRouteEnum.Performance,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <PerformanceOverviewPage />
          </Suspense>
        ),
      },
      {
        path: AnalyticsRouteEnum.Enforcement,
        children: [
          {
            index: true,
            loader: async () => {
              const tabList = await fetchEnforcementTabListQuery();
              if (!tabList?.tabs.length) {
                return redirect(`../${AnalyticsRouteEnum.Performance}`);
              }
              return redirect(tabList?.tabs[0].code);
            },
          },
          {
            path: ':tab',
          },
        ],
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <PerformanceEnforcementAnalysisPage />
          </Suspense>
        ),
      },
      {
        path: AnalyticsRouteEnum.CategoryShare,
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <PerformanceCategoryShareSnapshotPage />
          </Suspense>
        ),
      },
      {
        path: AnalyticsRouteEnum.SaturationRate,
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <PerformanceSaturationRatePage />
          </Suspense>
        ),
      },
      {
        path: AnalyticsRouteEnum.Seller,
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <PerformanceSellerAnalysisPage />
          </Suspense>
        ),
      },
      {
        path: AnalyticsRouteEnum.Compliance,
        element: (
          <Suspense fallback={<LoadingCircularProgress open />}>
            <PerformanceMarketplaceComplianceRatePage />
          </Suspense>
        ),
      },
      {
        path: AnalyticsRouteEnum.SocialMediaVideo,
        children: [
          {
            path: `${AnalyticsRouteEnum.EnforcementAnalysis}/:tab`,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <PiracySocialMediaPerformanceEnforcementAnalysisPage />
              </Suspense>
            ),
          },
          {
            path: AnalyticsRouteEnum.ComplianceRate,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <PiracySocialMediaPerformanceComplianceRatePage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: AnalyticsRouteEnum.WebsiteContent,
        children: [
          {
            path: `${AnalyticsRouteEnum.EnforcementAnalysis}/:tab`,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <PiracyWebsiteContentPerformanceEnforcementAnalysisPage />
              </Suspense>
            ),
          },
          {
            path: AnalyticsRouteEnum.CleanlinessRate,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <PiracyWebsiteContentPerformanceCleanlinessRatePage />
              </Suspense>
            ),
          },
          {
            path: AnalyticsRouteEnum.ComplianceRate,
            element: (
              <Suspense fallback={<LoadingCircularProgress open />}>
                <PiracyWebsiteContentPerformanceComplianceRatePage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: AnalyticsRouteEnum.CustomerReports,
    loader: async () => {
      const hasAccess = await PermissionMap.PerformanceReport();
      if (hasAccess) {
        return null; // bypass
      } else {
        return redirect(`/${RootRouteEnum.Home}`);
      }
    },
    element: (
      <Suspense fallback={<LoadingCircularProgress open />}>
        <PerformanceReportsPage />
      </Suspense>
    ),
  },
  {
    path: AnalyticsRouteEnum.BrandExposure,
    loader: async () => {
      const hasAccess = await PermissionMap.PerformanceBrandExposure();
      if (hasAccess) {
        return null; // bypass
      } else {
        return redirect(`/${RootRouteEnum.Home}`);
      }
    },
    element: (
      <Suspense fallback={<LoadingCircularProgress open />}>
        <PerformanceBrandExposurePage />
      </Suspense>
    ),
  },
];
